import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, Trash2, Users, ExternalLink, Calendar } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/data-table/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/events/")({ component: EventsIndex });

interface EventRow {
  id: number;
  slug: string;
  title: string;
  starts_at: number;
  ends_at: number | null;
  venue: string | null;
  fee_inr: number;
  status: "draft" | "published" | "completed" | "cancelled";
  register_open: number; // 0/1
  updated_at: number;
}

function EventsIndex() {
  const { can } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["events", "list"],
    queryFn: () => fetchAll<EventRow>("/admin/events"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/events/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["events"] }); },
  });
  const publish = useMutation({
    mutationFn: (id: number) => api.post(`/admin/events/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["events"] }); },
  });

  const columns: ColumnDef<EventRow, unknown>[] = [
    {
      accessorKey: "title", header: "Event",
      cell: ({ row }) => (
        <div>
          <Link to="/events/$id" as never params={{ id: String(row.original.id) }} className="font-medium hover:text-primary">
            {row.original.title}
          </Link>
          <p className="text-xs text-muted-foreground">{row.original.venue ?? "Venue TBD"}</p>
        </div>
      ),
    },
    {
      accessorKey: "starts_at", header: "Starts",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {format(row.original.starts_at * 1000, "MMM d, yyyy h:mma")}
        </div>
      ),
    },
    {
      accessorKey: "fee_inr", header: "Fee",
      cell: ({ row }) =>
        row.original.fee_inr === 0
          ? <span className="text-emerald-700 text-xs font-medium">Free</span>
          : <span className="font-mono text-sm">₹{(row.original.fee_inr / 100).toLocaleString("en-IN")}</span>,
    },
    {
      accessorKey: "status", header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      filterFn: (r, _i, v) => r.getValue("status") === v,
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem asChild>
              <Link to="/events/$id" as never params={{ id: String(row.original.id) }}>Edit</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/events/$id/registrations" as never params={{ id: String(row.original.id) }}>
                <Users className="h-3.5 w-3.5" /> Registrations
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`/events/${row.original.slug}`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" /> View on site
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.original.status !== "published" && (
              <DropdownMenuItem disabled={!can("events", "publish")} onClick={() => publish.mutate(row.original.id)}>
                Publish
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!can("events", "delete")}
              onClick={() => { if (confirm(`Delete "${row.original.title}"?`)) remove.mutate(row.original.id); }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Events"
        description="Workshops, hackathons, fests, and any registrable activity. Free events confirm immediately; paid ones go through Razorpay."
        actions={can("events", "create") && (
          <Link to="/events/new" as never><Button><Plus className="h-4 w-4" /> New event</Button></Link>
        )}
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by title or venue…"
        filters={[{ id: "status", label: "Status", options: [
          { value: "draft", label: "Draft" }, { value: "published", label: "Published" },
          { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No events yet."}
      />
    </div>
  );
}
